import React, { useState } from "react";
import axios from "axios";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useBooking } from "../../context";
import { API } from "../../lib/constants";

const DiscountPopup = () => {
  const { showDiscountPopup, setShowDiscountPopup, popupType } = useBooking();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const discountInfo = {
    normal_10: { title: "Get 10% Off", subtitle: "Your First Detail" },
    exit_15: { title: "Wait! Get 15% Off", subtitle: "Before You Go" },
    review_20: { title: "$20 Off", subtitle: "For Your Honest Review" }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscribers`, {
        email,
        discount_type: popupType,
        source: "popup"
      });
      
      if (response.data.already_subscribed) {
        toast.error("This email has already received a discount code.");
        setLoading(false);
        return;
      }
      
      setSubmitted(true);
      toast.success("Check your email for your discount code!");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const info = discountInfo[popupType] || discountInfo.normal_10;

  return (
    <Dialog open={showDiscountPopup} onOpenChange={setShowDiscountPopup}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-md" data-testid="discount-popup">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            <span className="gradient-text">{info.title}</span>
          </DialogTitle>
          <DialogDescription className="text-center text-lg text-slate-300">
            {info.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-slate-800 border-slate-700"
                data-testid="popup-email-input"
              />
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full btn-primary-glow text-slate-900 font-semibold py-3"
                data-testid="popup-submit-btn"
              >
                {loading ? "Sending..." : "Get My Discount"}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                No spam. Unsubscribe anytime.
              </p>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="bg-slate-800 rounded-lg p-6">
                <Mail className="w-12 h-12 text-cyan-400 mx-auto mb-3" />
                <p className="text-lg font-medium text-white mb-2">Check Your Email!</p>
                <p className="text-sm text-slate-400">
                  We've sent your exclusive discount code to <span className="text-cyan-400">{email}</span>
                </p>
              </div>
              <p className="text-sm text-slate-500">
                Didn't receive it? Check your spam folder or try again.
              </p>
              <Button 
                onClick={() => setShowDiscountPopup(false)}
                className="btn-primary-glow text-slate-900"
                data-testid="popup-close-btn"
              >
                Got It!
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DiscountPopup;
